import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Form } from "../../../components/Formulario/styled";
import Loading from "../../../components/Loading";
import Bioma from "../../../../models/Bioma";
import Especie from "../../../../models/Especie";
import Patrocinador from "../../../../models/Patrocinador";
import Apoiador from "../../../../models/Apoiador";
import CustomHandle from "../../../../utils/CustomHandle";
import Validacao from "../../validacao";
import { convertToBase64 } from "../../../../utils/conversoes";
import { estados } from "../../../../utils/data";
import pin_icon from "../../../../assets/icons/pin.svg";
import { linkValido, MENSAGEM_LINK } from "../../../../utils/links";
import { mensagemDeErro, respostaOk } from "../../../../utils/resposta";
import DicaImagem, { PERFIL_BANNER, PERFIL_FOTO_APOIADOR } from "../../../components/DicaImagem";

const LIMITE_IMAGEM = 10 * 1024 * 1024; // 10 MB

// Validações que o envio padrão (CustomHandle) não faz: link http/https e tamanho
// da imagem. Roda ANTES, mas junta com os campos obrigatórios vazios, para a
// pessoa ver tudo o que falta de uma vez (antes só aparecia o erro do link).
// Devolve true quando pode seguir para o envio.
function validarAntes(formId, { link, arquivo }, setErrors) {
  const formulario = document.getElementById(formId);
  const faltando = Validacao.validarCampos(formulario, {});
  // validarCampos só olha <input> e <select>: os <textarea> obrigatórios (a
  // declaração do apoiador) escapavam e só o servidor reclamava.
  let primeiroTextarea = null;
  formulario.querySelectorAll("textarea[required]").forEach((t) => {
    if (t.value.trim() !== "") return;
    const rotulo = formulario.querySelector(`label[for="${t.id}"]`)?.textContent.trim();
    faltando[t.name] = `O campo ${rotulo} é obrigatório`;
    if (!primeiroTextarea) primeiroTextarea = t;
  });
  const extras = {};
  if (link !== undefined && link.trim() !== "" && !linkValido(link)) extras.link = MENSAGEM_LINK;
  if (arquivo && !String(arquivo.type).startsWith("image/")) extras.foto = "O arquivo escolhido não é uma imagem.";
  else if (arquivo && arquivo.size > LIMITE_IMAGEM) extras.foto = "A imagem passa de 10 MB. Escolha uma menor.";

  const campos = { ...faltando, ...extras };
  const temErro = Object.values(campos).some((v) => v);
  if (!temErro) return true;

  setErrors(campos);
  // validarCampos já focou o primeiro <input> obrigatório vazio; senão, foca o textarea
  // vazio ou o campo do erro extra.
  const inputVazio = Object.keys(faltando).some((k) => k !== primeiroTextarea?.name);
  if (!inputVazio) (primeiroTextarea || document.getElementById(Object.keys(extras)[0]))?.focus();
  return false;
}

/**
 * Formulários de criar/editar os cadastros de conteúdo. São PÁGINAS (não modais):
 * só os campos, "Salvar" e "Cancelar". A lógica e as validações vêm dos modais que
 * existiam (campos, regras, base64 das imagens, link http/https, apoiador sem
 * organizacao_id nem link fixos, biomas da espécie).
 *
 * Cada formulário recebe `inicial` ({} ao criar), `aoSalvar()` e `cancelarPara`.
 */

// Estado comum a todos: erros por campo, "salvando" e o envio com a validação de
// campos obrigatórios do projeto (CustomHandle).
function useEnvio(formId) {
  const [errors, setErrors] = useState({});
  const [salvando, setSalvando] = useState(false);

  // Limpa o erro do campo assim que a pessoa volta a digitar nele.
  const limpar = (campo) => setErrors((e) => (e[campo] || e.geral ? { ...e, [campo]: "", geral: "" } : e));

  const enviar = (acao, aoSucesso) =>
    CustomHandle.submitForm(formId, acao, errors, setErrors, setSalvando, aoSucesso);

  return { errors, setErrors, salvando, limpar, enviar };
}

function Erro({ texto }) {
  return <span className="error" role="alert">{texto}</span>;
}

function Rodape({ salvando, cancelarPara, geral }) {
  return (
    <>
      {geral && <p className="geral" role="alert">{geral}</p>}
      <div className="acoes-form">
        <button type="submit" disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</button>
        <Link className="cancelar" to={cancelarPara}>Cancelar</Link>
      </div>
    </>
  );
}

// Pré-visualização da imagem escolhida (ou da que já está salva).
function usePrevia(arquivo, atual) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!arquivo) { setUrl(null); return undefined; }
    const novo = URL.createObjectURL(arquivo);
    setUrl(novo);
    return () => URL.revokeObjectURL(novo);
  }, [arquivo]);
  return url || atual || null;
}

// Converte a imagem para o formato que o backend espera ({ base64, tipo }). Se a
// conversão falhar, devolve um "resultado de erro" no mesmo formato do axios.
async function comImagem(payload, arquivo) {
  if (!arquivo) return { ok: true, payload };
  try {
    const [foto] = await convertToBase64([arquivo]);
    return { ok: true, payload: { ...payload, foto } };
  } catch (e) {
    return { ok: false, erro: { response: { data: { error: "Ocorreu um erro ao tratar a imagem. Tente novamente." } } } };
  }
}

// Campo de arquivo da imagem, com a orientação de tamanho ideal e o aviso (não
// bloqueante) sobre a imagem escolhida. A pré-visualização fica ao lado do
// formulário (PreviaBanner / PreviaApoiador), mostrando como a imagem aparece de verdade.
function CampoImagem({ rotulo, editando, arquivo, setArquivo, erro, limpar, perfil }) {
  return (
    <div className="form-group">
      <label htmlFor="foto">{rotulo}</label>
      <input
        id="foto" name="foto" type="file" accept="image/*" required={!editando}
        aria-describedby="foto-ajuda foto-dica"
        onChange={(e) => { setArquivo(e.target.files[0] || null); limpar("foto"); }}
      />
      <span id="foto-ajuda" className="ajuda">
        {editando ? "Deixe em branco para manter a imagem atual." : "Obrigatória."}
      </span>
      <DicaImagem id="foto-dica" perfil={perfil} arquivo={arquivo} />
      <Erro texto={erro} />
    </div>
  );
}

// Moldura de uma imagem (ou o convite para escolher uma).
function Moldura({ url, alt, classe }) {
  return (
    <div className={`moldura ${classe}`}>
      {url ? <img src={url} alt={alt} /> : <span>Escolha uma imagem</span>}
    </div>
  );
}

// "Como aparece": o banner na HOME do app e no Fórum/Perfil do site.
function PreviaBanner({ url }) {
  return (
    <aside className="previa-lateral" aria-label="Pré-visualização do banner">
      <h3>Como aparece</h3>
      <div>
        <span className="sub">Site</span>
        <Moldura url={url} alt="Banner como aparece no site" classe="site" />
      </div>
      <div>
        <span className="sub">Aplicativo Geoplantio</span>
        <Moldura url={url} alt="Banner como aparece no aplicativo" classe="app" />
      </div>
    </aside>
  );
}

// "Como aparece": o cartão do apoiador na HOME do app.
function PreviaApoiador({ url, nome, estado, descricao }) {
  return (
    <aside className="previa-lateral" aria-label="Pré-visualização do cartão do apoiador">
      <h3>Como aparece</h3>
      <div>
        <span className="sub">Aplicativo Geoplantio</span>
        <div className="cartao-app">
          <div className="perfil">
            {url ? <img className="avatar" src={url} alt="Foto do apoiador" /> : <span className="avatar vazio" aria-hidden="true" />}
            <div>
              <div className="nome">{nome.trim() || "Nome"}</div>
              <div className="local"><img src={pin_icon} alt="" />{estado.trim() || "Estado"}</div>
            </div>
          </div>
          <span className="desc">{descricao.trim() || "Declaração do apoiador"}</span>
        </div>
      </div>
    </aside>
  );
}

// ------------------------------------------------------------------- Bioma

export function FormBioma({ inicial, aoSalvar, cancelarPara }) {
  const [nome, setNome] = useState(inicial.nome || "");
  const { errors, salvando, limpar, enviar } = useEnvio("form-bioma");

  const salvar = (e) => {
    e.preventDefault();
    enviar(
      () => Bioma.create({ ...(inicial.id ? { id: inicial.id } : {}), nome: nome.trim() }),
      () => aoSalvar(),
    );
  };

  return (
    <Form id="form-bioma" onSubmit={salvar} noValidate>
      {salvando && <Loading titulo="Aguarde um momento" subtitulo="Salvando o bioma" />}
      <div>
        <div className="form-group">
          <label htmlFor="nome">Nome do bioma</label>
          <input
            id="nome" name="nome" type="text" required autoFocus maxLength={100} value={nome}
            placeholder="Ex.: Mata Atlântica" className={errors.nome ? "input-error" : undefined}
            onChange={(e) => { setNome(e.target.value); limpar("nome"); }}
          />
          <Erro texto={errors.nome} />
        </div>
        <Rodape salvando={salvando} cancelarPara={cancelarPara} geral={errors.geral} />
      </div>
    </Form>
  );
}

// ----------------------------------------------------------------- Espécie

export function FormEspecie({ inicial, aoSalvar, cancelarPara }) {
  const [comum, setComum] = useState(inicial.nome_comum || "");
  const [cientifico, setCientifico] = useState(inicial.nome_cientifico || "");
  const [marcados, setMarcados] = useState(() => (inicial.biomas || []).map((b) => b.id));
  const [biomas, setBiomas] = useState({ rows: [], carregando: true, erro: "" });
  const { errors, setErrors, salvando, limpar, enviar } = useEnvio("form-especie");

  // Todos os biomas são poucos (7): uma lista de caixas de seleção é mais clara
  // do que um campo de busca para ir adicionando um a um.
  useEffect(() => {
    let vivo = true;
    Bioma.listar(1, 100).then((r) => {
      if (!vivo) return;
      if (respostaOk(r)) setBiomas({ rows: r.data.rows, carregando: false, erro: "" });
      else setBiomas({ rows: [], carregando: false, erro: mensagemDeErro(r) });
    });
    return () => { vivo = false; };
  }, []);

  const alternar = (id) => {
    setMarcados((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));
    limpar("biomas");
  };

  const salvar = (e) => {
    e.preventDefault();
    if (marcados.length === 0) {
      setErrors({ ...errors, biomas: "Selecione pelo menos um bioma." });
      document.getElementById("biomas")?.focus();
      return;
    }
    enviar(
      () => Especie.create({
        ...(inicial.id ? { id: inicial.id } : {}),
        nome_comum: comum.trim(), nome_cientifico: cientifico.trim(), biomas: marcados,
      }),
      () => aoSalvar(),
    );
  };

  return (
    <Form id="form-especie" onSubmit={salvar} noValidate>
      {salvando && <Loading titulo="Aguarde um momento" subtitulo="Salvando a espécie" />}
      <div>
        <div className="form-group">
          <label htmlFor="nome_comum">Nome comum</label>
          <input
            id="nome_comum" name="nome_comum" type="text" autoFocus maxLength={100} value={comum}
            placeholder="Ex.: Ipê-amarelo (opcional)" className={errors.nome_comum ? "input-error" : undefined}
            onChange={(e) => { setComum(e.target.value); limpar("nome_comum"); }}
          />
          <Erro texto={errors.nome_comum} />
        </div>

        <div className="form-group">
          <label htmlFor="nome_cientifico">Nome científico</label>
          <input
            id="nome_cientifico" name="nome_cientifico" type="text" required maxLength={255} value={cientifico}
            placeholder="Ex.: Handroanthus albus" className={errors.nome_cientifico ? "input-error" : undefined}
            onChange={(e) => { setCientifico(e.target.value); limpar("nome_cientifico"); }}
          />
          <span className="ajuda">Único: não pode haver duas espécies com o mesmo nome científico.</span>
          <Erro texto={errors.nome_cientifico} />
        </div>

        <fieldset id="biomas" tabIndex={-1} className={errors.biomas ? "com-erro" : undefined} aria-describedby="biomas-erro">
          <legend>Biomas em que ocorre</legend>
          <div className="lista-biomas">
            {biomas.carregando && <span className="ajuda">Carregando biomas...</span>}
            {biomas.erro && <span className="error" role="alert">{biomas.erro}</span>}
            {biomas.rows.map((b) => (
              <label key={b.id} htmlFor={`bioma-${b.id}`}>
                <input id={`bioma-${b.id}`} type="checkbox" checked={marcados.includes(b.id)} onChange={() => alternar(b.id)} />
                {b.nome}
              </label>
            ))}
          </div>
          <span id="biomas-erro"><Erro texto={errors.biomas} /></span>
        </fieldset>

        <div className="form-group">
          <label htmlFor="nome-completo">Nome completo</label>
          <input id="nome-completo" className="nome-completo" disabled value={(comum.trim() ? `${comum.trim()} - ` : "") + cientifico.trim()} placeholder="Nome comum + nome científico" />
        </div>

        <Rodape salvando={salvando} cancelarPara={cancelarPara} geral={errors.geral} />
      </div>
    </Form>
  );
}

// ------------------------------------------------------------------ Banner

export function FormBanner({ inicial, aoSalvar, cancelarPara }) {
  const editando = Boolean(inicial.id);
  const [nome, setNome] = useState(inicial.nome || "");
  const [link, setLink] = useState(inicial.link || "");
  const [arquivo, setArquivo] = useState(null);
  const previa = usePrevia(arquivo, inicial.foto?.path);
  const { errors, setErrors, salvando, limpar, enviar } = useEnvio("form-banner");

  const salvar = (e) => {
    e.preventDefault();
    // O backend só aceita http/https (antes aceitava qualquer texto, como "asd").
    if (!validarAntes("form-banner", { link, arquivo }, setErrors)) return;
    enviar(async () => {
      const base = {
        ...(inicial.id ? { id: inicial.id } : {}),
        ...(inicial.solicitacao_id ? { solicitacao_id: inicial.solicitacao_id } : {}),
        nome: nome.trim(), link: link.trim(),
      };
      const r = await comImagem(base, arquivo);
      return r.ok ? Patrocinador.create(r.payload) : r.erro;
    }, () => aoSalvar());
  };

  return (
    <div className="layout-form">
      <Form id="form-banner" onSubmit={salvar} noValidate>
        {salvando && <Loading titulo="Aguarde um momento" subtitulo="Salvando o banner" />}
        <div>
          <div className="form-group">
            <label htmlFor="nome">Nome do patrocinador</label>
            <input
              id="nome" name="nome" type="text" required autoFocus maxLength={100} value={nome}
              className={errors.nome ? "input-error" : undefined}
              onChange={(e) => { setNome(e.target.value); limpar("nome"); }}
            />
            <Erro texto={errors.nome} />
          </div>

          <div className="form-group">
            <label htmlFor="link">Site do patrocinador</label>
            <input
              id="link" name="link" type="text" required value={link} placeholder="https://site-do-patrocinador.com.br"
              className={errors.link ? "input-error" : undefined}
              onChange={(e) => { setLink(e.target.value); limpar("link"); }}
            />
            <Erro texto={errors.link} />
          </div>

          <CampoImagem rotulo="Imagem do banner" editando={editando} arquivo={arquivo} setArquivo={setArquivo} erro={errors.foto} limpar={limpar} perfil={PERFIL_BANNER} />

          <Rodape salvando={salvando} cancelarPara={cancelarPara} geral={errors.geral} />
        </div>
      </Form>
      <PreviaBanner url={previa} />
    </div>
  );
}

// --------------------------------------------------------------- Apoiador

// Dois tipos de apoiador:
//  - DO APP (origem 'app'): veio de um pedido feito pelo app e aprovado em Pedidos.
//    Nome, estado e foto são os do PERFIL da pessoa (o backend sempre devolve os
//    atuais), então aparecem SÓ para leitura; o administrador edita apenas a
//    declaração e o link.
//  - MANUAL: empresa ou pessoa sem conta no app, cadastrada à mão: formulário completo.
export function FormApoiador({ inicial, aoSalvar, cancelarPara }) {
  const editando = Boolean(inicial.id);
  const doApp = editando && inicial.origem === "app";
  const [nome, setNome] = useState(inicial.nome || "");
  const [estado, setEstado] = useState(inicial.estado || "");
  const [link, setLink] = useState(inicial.link || "");
  const [descricao, setDescricao] = useState(inicial.descricao || "");
  const [arquivo, setArquivo] = useState(null);
  const previa = usePrevia(arquivo, inicial.foto?.path);
  const { errors, setErrors, salvando, limpar, enviar } = useEnvio("form-apoiador");

  const salvar = (e) => {
    e.preventDefault();
    if (!validarAntes("form-apoiador", { link, arquivo: doApp ? null : arquivo }, setErrors)) return;
    enviar(async () => {
      // Sem `organizacao_id` fixo (o painel antigo mandava 3, que não existe): sem ele
      // o backend assume o Plantio Brasil. O link é opcional: vazio vira "sem link" e
      // o app mostra o apoiador sem torná-lo clicável.
      const base = {
        ...(inicial.id ? { id: inicial.id } : {}),
        nome: nome.trim(), estado: estado.trim(), link: link.trim(), descricao: descricao.trim(),
      };
      // Do app: não há foto para enviar (é a do perfil) e o backend ignora nome/estado
      // (ficam os do perfil); eles seguem no corpo só porque o backend os exige.
      const r = doApp ? { ok: true, payload: base } : await comImagem(base, arquivo);
      return r.ok ? Apoiador.create(r.payload) : r.erro;
    }, () => aoSalvar());
  };

  return (
    <div className="layout-form">
      <Form id="form-apoiador" onSubmit={salvar} noValidate>
        {salvando && <Loading titulo="Aguarde um momento" subtitulo="Salvando o apoiador" />}
        <div>
          {doApp
            ? (
              <div className="somente-leitura" role="note">
                <strong>Vem do perfil da pessoa no app.</strong> Nome, estado e foto são os do perfil e mudam
                quando ela os altera. Aqui você edita só a declaração e o link.
              </div>
            )
            : !editando && <p className="ajuda">Use para empresas ou pessoas que não têm conta no app. Quem tem conta pede pelo app e você aprova em Pedidos.</p>}

          <div className="form-group">
            <label htmlFor="nome">Nome do apoiador</label>
            <input
              id="nome" name="nome" type="text" required={!doApp} autoFocus={!doApp} maxLength={100} value={nome}
              readOnly={doApp} aria-readonly={doApp} className={errors.nome ? "input-error" : doApp ? "somente-leitura-campo" : undefined}
              onChange={(e) => { setNome(e.target.value); limpar("nome"); }}
            />
            <Erro texto={errors.nome} />
          </div>

          <div className="form-group">
            <label htmlFor="estado">Estado do apoiador</label>
            <input
              id="estado" name="estado" type="text" list={doApp ? undefined : "estados-apoiador"} required={!doApp} autoComplete="off" maxLength={30} value={estado}
              readOnly={doApp} aria-readonly={doApp}
              placeholder="Escolha o estado" className={errors.estado ? "input-error" : doApp ? "somente-leitura-campo" : undefined}
              onChange={(e) => { setEstado(e.target.value); limpar("estado"); }}
            />
            {!doApp &&
              <datalist id="estados-apoiador">
                {estados.map((e) => <option key={e} value={e} />)}
              </datalist>
            }
            <Erro texto={errors.estado} />
          </div>

          <div className="form-group">
            <label htmlFor="link">Link do apoiador (opcional)</label>
            <input
              id="link" name="link" type="text" maxLength={100} value={link} placeholder="https://site-do-apoiador.com.br"
              className={errors.link ? "input-error" : undefined} autoFocus={doApp}
              onChange={(e) => { setLink(e.target.value); limpar("link"); }}
            />
            <span className="ajuda">Para onde levar quem tocar no apoiador no app. Deixe em branco se não houver.</span>
            <Erro texto={errors.link} />
          </div>

          <div className="form-group">
            <label htmlFor="descricao">Declaração do apoiador</label>
            <textarea
              id="descricao" name="descricao" required maxLength={255} value={descricao} placeholder="O que o apoiador declara sobre o projeto"
              className={errors.descricao ? "input-error" : undefined}
              onChange={(e) => { setDescricao(e.target.value); limpar("descricao"); }}
            />
            <span className="ajuda">{descricao.length}/255</span>
            <Erro texto={errors.descricao} />
          </div>

          {!doApp &&
            <CampoImagem rotulo="Foto ou logotipo do apoiador" editando={editando} arquivo={arquivo} setArquivo={setArquivo} erro={errors.foto} limpar={limpar} perfil={PERFIL_FOTO_APOIADOR} />
          }

          <Rodape salvando={salvando} cancelarPara={cancelarPara} geral={errors.geral} />
        </div>
      </Form>
      <PreviaApoiador url={previa} nome={nome} estado={estado} descricao={descricao} />
    </div>
  );
}
